import type { PlayerLocationDeltas } from '@repo/types/maps/map-types';
import type { GameSessionInternal } from '@repo/types/sessions/session-types';
import { getEventCategoryIcon, hasEventPosition, isGameEvent } from '@repo/utility/events';
import type { GeoJSONSource, Map } from 'maplibre-gl';
import * as MapLibre from 'maplibre-gl';
import { getEventIconSvg } from '../hooks/fontUtils';
import { looksLikeLatLng, transformGameCoord } from '../utils/coordinate-transform';
import {
    EVENT_CATEGORY_COLORS,
    EVENT_CIRCLE_LAYER_ID,
    EVENT_CIRCLE_OPACITY,
    EVENT_CIRCLE_RADIUS,
    EVENT_CIRCLE_STROKE_COLOR,
    EVENT_CIRCLE_STROKE_WIDTH,
    EVENT_ICON_LAYER_ID,
    EVENT_SOURCE_ID,
} from './trail-rendering';

const EVENT_ICON_IMAGE_SIZE = 20;

/**
 * Renders game event markers on the map as colored circles with category icons.
 */
export class EventMarkerRenderer {
    private map: Map;
    private loadedIcons = new Set<string>();
    private popup: MapLibre.Popup;
    private cachedLooksLikeLatLng: boolean = false;
    private cachedSessionUuid: string = '';
    private hoverHandlersAttached: boolean = false;
    private showPopupHandler: ((e: MapLibre.MapMouseEvent & { features?: MapLibre.GeoJSONFeature[] }) => void) | null =
        null;
    private hidePopupHandler: (() => void) | null = null;
    private pendingRender: { session: GameSessionInternal; deltas: PlayerLocationDeltas | null } | null = null;
    private deferredInitScheduled = false;

    constructor(map: Map) {
        this.map = map;
        this.popup = new MapLibre.Popup({
            closeButton: false,
            closeOnClick: false,
            offset: 12,
            className: 'event-marker-popup',
        });
        this.initializeLayers();
    }

    /**
     * Attempt to add source and layers. Returns true if successful.
     * Does NOT defer — callers handle scheduling retries.
     */
    private initializeLayers(): boolean {
        if (!this.map || this.map._removed) return false;

        try {
            if (!this.map.getSource(EVENT_SOURCE_ID)) {
                this.map.addSource(EVENT_SOURCE_ID, {
                    type: 'geojson',
                    data: { type: 'FeatureCollection', features: [] },
                });
            }

            if (!this.map.getLayer(EVENT_CIRCLE_LAYER_ID)) {
                this.map.addLayer({
                    id: EVENT_CIRCLE_LAYER_ID,
                    type: 'circle',
                    source: EVENT_SOURCE_ID,
                    paint: {
                        'circle-color': ['get', 'color'],
                        'circle-radius': EVENT_CIRCLE_RADIUS,
                        'circle-opacity': EVENT_CIRCLE_OPACITY,
                        'circle-stroke-color': EVENT_CIRCLE_STROKE_COLOR,
                        'circle-stroke-width': EVENT_CIRCLE_STROKE_WIDTH,
                    },
                });
            }

            if (!this.map.getLayer(EVENT_ICON_LAYER_ID)) {
                this.map.addLayer({
                    id: EVENT_ICON_LAYER_ID,
                    type: 'symbol',
                    source: EVENT_SOURCE_ID,
                    layout: {
                        'icon-image': ['get', 'iconImage'],
                        'icon-size': 0.6,
                        'icon-allow-overlap': true,
                        'icon-ignore-placement': true,
                    },
                });
            }

            this.setupHoverHandlers();
        } catch {
            return false;
        }

        return !!this.map.getSource(EVENT_SOURCE_ID) && !!this.map.getLayer(EVENT_CIRCLE_LAYER_ID);
    }

    /**
     * Schedule deferred initialization via multiple events to cover all timing scenarios.
     * 'load' fires on first style load; 'idle' fires after any render cycle completes.
     */
    private scheduleDeferredInit(): void {
        if (this.deferredInitScheduled) return;
        this.deferredInitScheduled = true;

        const tryInit = () => {
            this.deferredInitScheduled = false;
            if (!this.map || this.map._removed) return;

            if (this.initializeLayers() && this.pendingRender) {
                const { session, deltas } = this.pendingRender;
                this.pendingRender = null;
                this.render(session, deltas);
            }
        };

        // Use both events — whichever fires first wins, the other is a no-op
        this.map.once('load', tryInit);
        this.map.once('idle', tryInit);
    }

    private setupHoverHandlers(): void {
        if (this.hoverHandlersAttached) return;
        this.hoverHandlersAttached = true;

        this.showPopupHandler = (e: MapLibre.MapMouseEvent & { features?: MapLibre.GeoJSONFeature[] }) => {
            if (!e.features?.length) return;
            const feature = e.features[0];
            if (!feature) return;
            const eventName = feature.properties?.eventName;
            if (!eventName || !feature.geometry || feature.geometry.type !== 'Point') return;

            this.map.getCanvas().style.cursor = 'pointer';
            const coords = feature.geometry.coordinates.slice() as [number, number];
            const safeText = document.createElement('div');
            safeText.textContent = eventName;
            safeText.style.cssText =
                'background:rgba(23,23,23,0.9);color:#fff;padding:4px 10px;border-radius:6px;font-size:12px;font-weight:500;white-space:nowrap;backdrop-filter:blur(8px);';
            this.popup.setLngLat(coords).setDOMContent(safeText).addTo(this.map);
        };

        this.hidePopupHandler = () => {
            this.map.getCanvas().style.cursor = '';
            this.popup.remove();
        };

        this.map.on('mouseenter', EVENT_CIRCLE_LAYER_ID, this.showPopupHandler);
        this.map.on('mouseleave', EVENT_CIRCLE_LAYER_ID, this.hidePopupHandler);
        this.map.on('mouseenter', EVENT_ICON_LAYER_ID, this.showPopupHandler);
        this.map.on('mouseleave', EVENT_ICON_LAYER_ID, this.hidePopupHandler);
    }

    private removeHoverHandlers(): void {
        if (!this.hoverHandlersAttached) return;

        if (this.showPopupHandler) {
            this.map.off('mouseenter', EVENT_CIRCLE_LAYER_ID, this.showPopupHandler);
            this.map.off('mouseenter', EVENT_ICON_LAYER_ID, this.showPopupHandler);
        }
        if (this.hidePopupHandler) {
            this.map.off('mouseleave', EVENT_CIRCLE_LAYER_ID, this.hidePopupHandler);
            this.map.off('mouseleave', EVENT_ICON_LAYER_ID, this.hidePopupHandler);
        }

        this.showPopupHandler = null;
        this.hidePopupHandler = null;
        this.hoverHandlersAttached = false;
    }

    private loadIconImage(iconName: string): void {
        const imageId = `event-icon-${iconName}`;
        if (this.loadedIcons.has(iconName)) return;

        this.loadedIcons.add(iconName);

        try {
            if (this.map.hasImage(imageId)) return;
        } catch {
            // ignore — map may not be ready
        }

        getEventIconSvg(iconName, EVENT_ICON_IMAGE_SIZE)
            .then(svg => {
                if (!svg) return;

                const svgDataUrl = `data:image/svg+xml;base64,${btoa(svg)}`;
                const img = new Image(EVENT_ICON_IMAGE_SIZE, EVENT_ICON_IMAGE_SIZE);
                img.onload = () => {
                    try {
                        if (!this.map.hasImage(imageId)) {
                            this.map.addImage(imageId, img, { pixelRatio: 1 });
                        }
                        this.map.triggerRepaint();
                    } catch {
                        // ignore
                    }
                };
                img.onerror = () => {
                    this.loadedIcons.delete(iconName);
                };
                img.src = svgDataUrl;
            })
            .catch(() => {
                this.loadedIcons.delete(iconName);
                if (process.env.NODE_ENV === 'development') {
                    console.warn(`[EventMarkerRenderer] Failed to render icon: ${iconName}`);
                }
            });
    }

    public render(session: GameSessionInternal | null, deltas: PlayerLocationDeltas | null): void {
        if (!session || !this.map || this.map._removed) {
            this.clear();
            return;
        }

        if (!this.map.getSource(EVENT_SOURCE_ID) || !this.map.getLayer(EVENT_CIRCLE_LAYER_ID)) {
            if (!this.initializeLayers()) {
                this.pendingRender = { session, deltas };
                this.scheduleDeferredInit();
                return;
            }
        }

        const source = this.map.getSource(EVENT_SOURCE_ID) as GeoJSONSource;
        if (!source) return;

        const events = session.events;
        if (!events || events.length === 0) {
            this.clear();
            return;
        }

        const trail = session.trail;

        if (this.cachedSessionUuid !== session.uuid) {
            this.cachedSessionUuid = session.uuid;
            this.cachedLooksLikeLatLng = looksLikeLatLng(trail);
        }
        const isLatLng = this.cachedLooksLikeLatLng;

        const features: GeoJSON.Feature[] = [];

        for (const event of events) {
            if (!isGameEvent(event.eventType)) continue;
            if (!hasEventPosition(event.x, event.y, event.z)) continue;

            const coord = transformGameCoord(event, deltas, isLatLng);

            const iconName = getEventCategoryIcon(event.eventCategory);
            const color = EVENT_CATEGORY_COLORS[event.eventCategory] || EVENT_CATEGORY_COLORS.general;
            const imageId = `event-icon-${iconName}`;

            this.loadIconImage(iconName);

            features.push({
                type: 'Feature',
                properties: {
                    color,
                    iconImage: imageId,
                    eventName: event.eventName,
                    category: event.eventCategory,
                },
                geometry: {
                    type: 'Point',
                    coordinates: coord,
                },
            });
        }

        try {
            source.setData({ type: 'FeatureCollection', features });
            if (this.map.getLayer(EVENT_CIRCLE_LAYER_ID)) this.map.moveLayer(EVENT_CIRCLE_LAYER_ID);
            if (this.map.getLayer(EVENT_ICON_LAYER_ID)) this.map.moveLayer(EVENT_ICON_LAYER_ID);
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                console.warn('[EventMarkerRenderer] Failed to update source:', error);
            }
        }
    }

    public clear(): void {
        this.pendingRender = null;
        try {
            this.popup.remove();
            const source = this.map.getSource(EVENT_SOURCE_ID) as GeoJSONSource;
            if (source) {
                source.setData({ type: 'FeatureCollection', features: [] });
            }
        } catch {
            // ignore
        }
    }

    public cleanup(): void {
        if (!this.map || this.map._removed) return;

        this.pendingRender = null;
        this.deferredInitScheduled = false;
        this.removeHoverHandlers();
        this.popup.remove();

        try {
            if (this.map.getLayer(EVENT_ICON_LAYER_ID)) {
                this.map.removeLayer(EVENT_ICON_LAYER_ID);
            }
            if (this.map.getLayer(EVENT_CIRCLE_LAYER_ID)) {
                this.map.removeLayer(EVENT_CIRCLE_LAYER_ID);
            }
            if (this.map.getSource(EVENT_SOURCE_ID)) {
                this.map.removeSource(EVENT_SOURCE_ID);
            }
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                console.warn('[EventMarkerRenderer] Failed to cleanup:', error);
            }
        }

        this.loadedIcons.clear();
    }
}
